/**
 * The one status-tone registry (TMMAPP-0070, TMM-B2). Every status the app paints has a tone, and
 * the same status has the same tone on every screen; `StatusChip` and the cashflow chart's markers
 * both read `toneFor`. The tones (TMM profile §5 annex): success = done or in good standing, info =
 * waiting on the system, warning = needs someone's action, error = failed or blocked, neutral =
 * ended, inactive or closed, draft = outlined, not yet in effect (an outlined chip means draft and
 * nothing else). One visible label is never in two tones (profile 1.1): where one code means two
 * things (`REVOKED`), each meaning has its own label.
 *
 * Each domain's map is typed over the vendored contract's real enum (`src/api/generated/`), so a
 * re-vendor that adds, renames or drops a status is a compile error here, never a silent default.
 * An unknown value at run time (the wire is open) is `neutral`, and `StatusChip` words it "Unknown
 * status" rather than printing the code.
 */
import type {
  AccountResponse,
  ConnectorMetadataView,
  EntitlementItem,
  HoldingResponse,
  ImportBatchResponse,
  ListItem as VirtualAccountListItem,
  MeetingInviteView,
  MeetingItemView,
  MeetingOccurrenceView,
  MeetingResponse,
  MemberStatementView,
  MemberTicket,
  OwnerRegisterLine,
  PlannedOccurrenceView,
  SeriesResponse,
  ShareResponse,
} from '@/api/generated';
import type { AppTokens } from './tokens';

export type Tone = 'success' | 'info' | 'warning' | 'error' | 'neutral' | 'draft';

type Of<T, K extends keyof T> = NonNullable<T[K]>;

/**
 * A meeting occurrence is `SCHEDULED` / `HELD` / `SKIPPED` (the contract); the meeting itself is
 * `SCHEDULED` / `CANCELLED`; `IN_PROGRESS` is the client's name for a held meeting that has started
 * and not ended (`live` on the calendar entry, `heldStartedAt` without `heldEndedAt` on a session).
 */
type MeetingStatus =
  Of<MeetingOccurrenceView, 'status'> | Of<MeetingResponse, 'status'> | 'IN_PROGRESS';

/** Domain → its status enum, as the contract spells it. */
interface StatusEnums {
  /** A planned occurrence's state (series projection, register, calendar, sheet). */
  occurrence: Of<PlannedOccurrenceView, 'state'>;
  meeting: MeetingStatus;
  meetingItem: Of<MeetingItemView, 'status'>;
  connector: Of<ConnectorMetadataView, 'status'>;
  importBatch: Of<ImportBatchResponse, 'status'>;
  /** A bank transaction on the owner register. */
  transaction: Of<OwnerRegisterLine, 'status'>;
  /** A billing statement's charge. */
  charge: Of<MemberStatementView, 'chargeStatus'>;
  /** A subscription entitlement (own or sponsored seat). */
  subscription: Of<EntitlementItem, 'status'>;
  series: Of<SeriesResponse, 'status'>;
  holding: Of<HoldingResponse, 'status'>;
  share: Of<ShareResponse, 'status'>;
  /** An owner-side bank account (`OPEN` is worded "Active" so it never shares "Open" with an item). */
  account: Of<AccountResponse, 'status'>;
  virtualAccount: Of<VirtualAccountListItem, 'status'>;
  /** An envelope carries no status enum: the client reads `archivedAt` (set = `ARCHIVED`). */
  envelope: 'ACTIVE' | 'ARCHIVED';
  /**
   * An AI request's outcome. The contract types it as an open string; these are the four values
   * tmm-api records (design §6.12), so a fifth one is "Unknown status" until it is added here.
   */
  aiRequest: 'PENDING' | 'PROPOSED' | 'NO_SUGGESTION' | 'FAILED';
  /** A meeting invitee's invitation email (TMMAPP-0082: it was a bare catalog word on a chip). */
  invite: Of<MeetingInviteView, 'status'>;
  /**
   * A transaction the bank still holds as pending (the connected account's pending strip). The
   * contract carries no status for it; every such row is pending, worded like a charge's.
   */
  bankPending: 'PENDING';
  /**
   * An envelope register line that was undone. A yes/no flag on the line (`reversed`), not an
   * enum, so only the marked state is a status; a live line shows no chip.
   */
  registerLine: 'REVERSED';
  /**
   * A bank-linked account card's connection status (TMMAPP-0120), derived from the connector's
   * `status` and `initialSync` (`connectionStateOf`): not a contract enum.
   */
  connection: 'CONNECTED' | 'SYNCING' | 'NEEDS_ATTENTION';
  /**
   * A support case's or feature request's public status (TMMAPP-0142 U2). `WAITING_ON_YOU` needs the
   * person's reply (warning); a ticket with the support team is info; resolved or shipped is done.
   */
  ticket: Of<MemberTicket, 'status'>;
}

export type StatusDomain = keyof StatusEnums;
export type StatusOf<D extends StatusDomain> = StatusEnums[D];

type ToneMaps = { [D in StatusDomain]: Record<StatusEnums[D], Tone> };

const TONE_MAPS: ToneMaps = {
  occurrence: {
    PLANNED: 'info',
    // TMMAPP-0173 (MM-D-153): part of a paycheck has arrived; it is still coming, as PLANNED is.
    PARTIAL: 'info',
    MATCHED: 'success',
    POSTED: 'success',
    MISSED: 'warning',
    SUPERSEDED: 'neutral',
    CANCELLED: 'neutral',
  },
  meeting: {
    SCHEDULED: 'info',
    IN_PROGRESS: 'info',
    HELD: 'success',
    SKIPPED: 'neutral',
    CANCELLED: 'neutral',
  },
  meetingItem: {
    AGREED: 'success',
    DONE: 'success',
    MET: 'success',
    ANSWERED: 'success',
    REVISED: 'info',
    OPEN: 'warning',
    DROPPED: 'neutral',
    RETIRED: 'neutral',
  },
  connector: {
    ACTIVE: 'success',
    LOGIN_REQUIRED: 'warning',
    PENDING_EXPIRATION: 'warning',
    REVOKED: 'error',
    REMOVED: 'neutral',
  },
  importBatch: {
    COMMITTED: 'success',
    UPLOADED: 'info',
    PREVIEWED: 'info',
    FAILED: 'error',
    DISCARDED: 'neutral',
  },
  transaction: {
    POSTED: 'success',
    VOID: 'neutral',
  },
  charge: {
    PAID: 'success',
    PENDING: 'info',
    ACTION_REQUIRED: 'warning',
    ATTENTION: 'warning',
    FAILED: 'error',
    NONE: 'neutral',
    WAIVED: 'neutral',
  },
  subscription: {
    ACTIVE: 'success',
    ENDING: 'info',
    ENDED: 'neutral',
  },
  series: {
    ACTIVE: 'success',
    ENDED: 'neutral',
    CANCELLED: 'neutral',
  },
  holding: {
    ACTIVE: 'success',
    DISPOSED: 'neutral',
  },
  share: {
    ACTIVE: 'success',
    INVITED: 'info',
    // The owner ended the access: ended, not failed ("Removed", like the connector's REMOVED). The
    // connector's REVOKED is a bank withdrawing access, an error labelled "Access revoked" (profile §5).
    REVOKED: 'neutral',
    EXPIRED: 'neutral',
  },
  account: {
    OPEN: 'success',
    CLOSED: 'neutral',
  },
  virtualAccount: {
    ACTIVE: 'success',
    ARCHIVED: 'neutral',
  },
  envelope: {
    ACTIVE: 'success',
    ARCHIVED: 'neutral',
  },
  aiRequest: {
    PENDING: 'info',
    PROPOSED: 'success',
    NO_SUGGESTION: 'neutral',
    FAILED: 'error',
  },
  invite: {
    PENDING: 'info',
    SENT: 'success',
    FAILED: 'error',
    CANCELLED: 'neutral',
  },
  bankPending: { PENDING: 'info' },
  registerLine: { REVERSED: 'neutral' },
  connection: { CONNECTED: 'success', SYNCING: 'info', NEEDS_ATTENTION: 'warning' },
  ticket: {
    SUBMITTED: 'info',
    UNDER_REVIEW: 'info',
    PLANNED: 'info',
    IN_PROGRESS: 'info',
    OPEN: 'info',
    WAITING_ON_YOU: 'warning',
    SHIPPED: 'success',
    RESOLVED: 'success',
    NOT_PLANNED: 'neutral',
    DUPLICATE: 'neutral',
    CLOSED: 'neutral',
  },
};

/** Whether `status` is a value this registry knows for `domain` (the contract's, not the wire's). */
export function isKnownStatus<D extends StatusDomain>(
  domain: D,
  status: string | null | undefined,
): status is StatusOf<D> & string {
  return typeof status === 'string' && Object.hasOwn(TONE_MAPS[domain], status);
}

/** The tone of `status` in `domain`; anything the registry does not know is `neutral`. */
export function toneFor<D extends StatusDomain>(
  domain: D,
  status: string | null | undefined,
): Tone {
  if (!isKnownStatus(domain, status)) return 'neutral';
  return (TONE_MAPS[domain] as Record<string, Tone>)[status] ?? 'neutral';
}

/** Every known status of a domain, in the registry's order (tests walk this). */
export function statusesOf<D extends StatusDomain>(domain: D): StatusOf<D>[] {
  return Object.keys(TONE_MAPS[domain]) as StatusOf<D>[];
}

export const STATUS_DOMAINS = Object.keys(TONE_MAPS) as StatusDomain[];

/**
 * A status worded by i18next `context` as well as its own key (`status.<domain>.<STATUS>_<context>`):
 * an outgoing part-paid occurrence reads "Partly paid" (TMMAPP-0173 item 1). The registry's tone is
 * the status's, whatever the wording.
 */
export const STATUS_CONTEXTS: Readonly<
  Partial<Record<StatusDomain, Record<string, readonly string[]>>>
> = { occurrence: { PARTIAL: ['expense'] } };

type Painted = 'success' | 'info' | 'warning' | 'error';
const isPainted = (tone: Tone): tone is Painted => tone !== 'neutral' && tone !== 'draft';

/**
 * A tone's chip colours, every one a guaranteed pair (no new contrast pair): success / info /
 * warning / error paint `<tone>Text` on `<tone>Soft`; neutral is the default chip's `text` on opaque
 * `surface2`; draft is outlined — transparent, a `lineStrong` border, `text` ink.
 */
export function toneColours(
  tokens: AppTokens,
  tone: Tone,
): { background: string; ink: string; border: string | null } {
  if (isPainted(tone)) {
    return { background: tokens[`${tone}Soft`], ink: tokens[`${tone}Text`], border: null };
  }
  if (tone === 'draft') {
    return { background: 'transparent', ink: tokens.text, border: tokens.lineStrong };
  }
  return { background: tokens.surface2, ink: tokens.text, border: null };
}

/** A chart marker's paint for a tone: `<tone>Fg`; neutral and draft are `lineStrong`. */
export function toneMarker(tokens: AppTokens, tone: Tone): string {
  return isPainted(tone) ? tokens[`${tone}Fg`] : tokens.lineStrong;
}
